import { useQuery } from "@tanstack/react-query";
import * as AgenticV2API from "@/services/api/agentic-v2";
import {
  parseThreadArtifactCardContent,
  readArtifactId,
  threadMessageJsonContent,
} from "@/services/api/agentic-v2/threadArtifactContent";
import type { ThreadMessage } from "@/services/api/agentic-v2/types";
import { ArtifactCard } from "@/components/artifacts/ArtifactCard";
import type { ThreadTimestampDisplay } from "./threadTimestampUtils";
import { ThreadTimestamp } from "./ThreadTimestamp";

export interface ThreadArtifactCardProps {
  message: ThreadMessage;
  timestampDisplay: ThreadTimestampDisplay | null;
}

/** Renders an artifact_card message, fetching the artefact body when needed. */
export function ThreadArtifactCard({
  message,
  timestampDisplay,
}: ThreadArtifactCardProps) {
  const content = threadMessageJsonContent(message.content);
  const parsed = content ? parseThreadArtifactCardContent(content) : null;
  const embedded = parsed?.artifact;
  const artifactId = content ? readArtifactId(content) : null;

  const { data: fetched, isLoading } = useQuery({
    queryKey: ["agentic-v2", "artifact", artifactId],
    queryFn: () => AgenticV2API.getArtifact(artifactId!),
    enabled: content !== null && !embedded && artifactId !== null,
    retry: false,
    staleTime: 60_000,
  });

  const artifact = embedded ?? fetched;
  if (artifact) {
    return (
      <ArtifactCard
        artifact={artifact}
        variant="compact"
        timestampSlot={
          timestampDisplay ? (
            <ThreadTimestamp display={timestampDisplay} />
          ) : undefined
        }
      />
    );
  }

  if (isLoading) {
    return (
      <p
        className="px-3 py-2 text-xs text-slate-500"
        data-testid="thread-artifact-loading"
      >
        Loading artefact…
      </p>
    );
  }

  return (
    <p
      className="px-3 py-2 text-xs text-slate-500"
      data-testid="thread-artifact-unavailable"
    >
      Artefact unavailable.
    </p>
  );
}
